import type { AdminCapability } from 'common/models/admin/rbac';

export interface AdminNavChild {
  label: string;
  to: string;
}

export interface AdminNavItem {
  label: string;
  to: string;
  requires?: AdminCapability;
  children?: AdminNavChild[];
}

export const adminNavItems: AdminNavItem[] = [
  { label: 'Dashboard', to: '/' },
  { label: 'Sessions', to: '/analytics/sessions', requires: 'analytics:view' },
  {
    label: 'Analytics',
    to: '/analytics',
    requires: 'analytics:view',
    children: [
      { label: 'Overview', to: '/analytics' },
      { label: 'Product', to: '/analytics/product' },
    ],
  },
  {
    label: 'Organizations',
    to: '/organizations',
    requires: 'org:view',
    children: [
      { label: 'Active Orgs', to: '/organizations' },
      { label: 'Deleted Orgs', to: '/organizations/deleted' },
    ],
  },
  {
    label: 'Fleet',
    to: '/fleet',
    requires: 'fleet:view',
    children: [
      { label: 'Overview', to: '/fleet' },
      { label: 'Tasks', to: '/fleet/tasks' },
      { label: 'Regions', to: '/fleet/regions' },
      { label: 'Trends', to: '/fleet/trends' },
    ],
  },
  { label: 'Test runs', to: '/test-runs', requires: 'test-runs:view' },
  { label: 'Users', to: '/users', requires: 'user:view' },
];

export function visibleNavItems(can: (capability: AdminCapability) => boolean): AdminNavItem[] {
  return adminNavItems.filter((item) => !item.requires || can(item.requires));
}

const matchesPath = (pathname: string, to: string) => pathname === to || pathname.startsWith(`${to}/`);

/** A section owns its own paths, except those under a more specific top-level item (Sessions under /analytics). */
export function isNavSectionActive(
  item: AdminNavItem,
  pathname: string,
  items: AdminNavItem[] = adminNavItems,
): boolean {
  const ownsPath = [item.to, ...(item.children ?? []).map((child) => child.to)].some((to) =>
    matchesPath(pathname, to),
  );
  return (
    ownsPath &&
    !items.some(
      (other) =>
        other.to.length > item.to.length &&
        other.to.startsWith(`${item.to}/`) &&
        matchesPath(pathname, other.to),
    )
  );
}
